import { useState, useEffect, useRef, useCallback } from 'react';

const FORCE_MIN_OPEN_DISTANCE = 10;
const DEFAULT_THRESHOLD = 75;
/**
 * Hook to track scroll position for TilesContainer visibility
 * @param {Object} initiatorRef - Reference to the scrollable element that initiates visibility changes
 * @param {Object} animatedRef - Reference to the element being animated
 * @param {number} threshold - Distance from top to trigger animation (in px)
 * @param {Array} deps - Dependencies array that will trigger a re-check of scroll position when changed
 * @returns {Array} - [visibility state, function to force visibility]
 */
function useTileContainerScroll({
  initiatorRef,
  animatedRef,
  threshold = DEFAULT_THRESHOLD,
  deps = [],
}) {
  const [isVisible, setIsVisible] = useState(true);
  const prevVisibleRef = useRef(true);
  const isTransitioningRef = useRef(false);

  const handleScroll = useCallback(() => {
    const scrollTop = initiatorRef?.current?.scrollTop;
    if (scrollTop === undefined) return;
    const forceOpen = scrollTop <= FORCE_MIN_OPEN_DISTANCE;
    // Don't process scroll during transition
    if (isTransitioningRef.current && !forceOpen) return;

    const newIsVisible = scrollTop <= threshold || forceOpen;
    if (newIsVisible !== prevVisibleRef.current) {
      prevVisibleRef.current = newIsVisible;
      setIsVisible(newIsVisible);
    }
  }, [initiatorRef, threshold]);

  // Memoize event handlers with useCallback
  const handleTransitionEnd = useCallback((event) => {
    if (event.propertyName === 'max-height') {
      isTransitioningRef.current = false;
    }
  }, []);

  const handleTransitionStart = useCallback((event) => {
    if (event.propertyName === 'max-height') {
      isTransitioningRef.current = true;
    }
  }, []);

  // Add and remove event listeners
  useEffect(() => {
    const initiatorElement = initiatorRef?.current;
    const animatedElement = animatedRef?.current;

    if (initiatorElement) {
      initiatorElement.addEventListener('scroll', handleScroll, {
        passive: true,
      });
    }

    if (animatedElement) {
      animatedElement.addEventListener(
        'transitionstart',
        handleTransitionStart
      );
      animatedElement.addEventListener('transitionend', handleTransitionEnd);
    }

    return () => {
      if (initiatorElement) {
        initiatorElement.removeEventListener('scroll', handleScroll);
      }
      if (animatedElement) {
        animatedElement.removeEventListener(
          'transitionstart',
          handleTransitionStart
        );
        animatedElement.removeEventListener(
          'transitionend',
          handleTransitionEnd
        );
      }
    };
  });

  useEffect(() => {
    // Run initial check
    handleScroll();
  }, [handleScroll, ...deps]);

  const forceVisibility = useCallback(() => {
    prevVisibleRef.current = true;
    setIsVisible(true);
  }, []);

  return [isVisible, forceVisibility];
}

export default useTileContainerScroll;
